'use client';

import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { addSeconds, format } from 'date-fns';
import { formatDistance, formatPace, speedToPace } from '@/utils/distanceUtils';
import type { Lap } from '@trainme/db/client'; // Changed import from StravaLap to Lap
import { useEffect, useRef, useState } from 'react';
import { useUnitSystem } from '@/app/hooks/useUnitSystem';

type ProcessedLap = Lap & { averagePace: number | null };

type Props = {
  laps: Lap[]; // Changed to Lap[]
};

export default function ActivityLaps({ laps }: Props) {
  const unitSystem = useUnitSystem();
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });
  const barSize = 20;

  const processedLaps: ProcessedLap[] = (laps || []).map((lap) => ({
    ...lap,
    averagePace: lap.averageSpeed ? speedToPace(lap.averageSpeed) : 0,
  }));

  useEffect(() => {
    const node = chartRef.current;
    if (!node) return undefined;

    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      setChartSize({
        width: rect.width,
        height: rect.height,
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={chartRef} className='w-full flex flex-col h-full overflow-hidden'>
      <div className='grid grid-cols-12 gap-2 items-start text-[0.8em] bg-white dark:bg-gray-800 pb-2 shrink-0'>
        <div className='col-span-8 grid grid-cols-5'>
          <div className='w-8'>Lap</div>
          <div>Time</div>
          <div>
            Dist <br /> ({unitSystem === 'scientific' ? 'km' : 'mi'})
          </div>
          <div>
            Pace <br /> ({unitSystem === 'scientific' ? 'min/km' : 'min/mi'})
          </div>
          <div>
            Avg HR <br /> (bpm)
          </div>
        </div>
        <div className='col-span-4 pl-4'>Pace</div>
      </div>
      <div className='flex-1 overflow-y-auto scroll'>
        <div className='grid grid-cols-12 gap-2 h-full'>
          <div className='col-span-8 h-full py-2 flex flex-col justify-between text-[0.8em] font-monospace'>
            {processedLaps?.map((lap, index) => (
              <div
                className='gap-2 grid grid-cols-5'
                key={lap.id || `lap-${index}-${lap.elapsedTime}`}
              >
                <div>{index + 1}</div>
                <div className='text-justify w-full'>
                  {format(
                    addSeconds(new Date(0), lap.elapsedTime ?? 0),
                    'mm:ss'
                  )}
                </div>
                <div className='text-justify w-full'>
                  {formatDistance(lap.distance ?? null, unitSystem)}
                </div>
                <div className='text-justify w-full'>
                  {formatPace(lap.averagePace, unitSystem)}
                </div>
                <div className='text-justify w-full'>
                  {lap.averageHeartrate?.toFixed(0)}
                </div>
              </div>
            ))}
          </div>
          <div className='col-span-4'>
            {chartSize.width > 0 && chartSize.height > 0 && (
              <ResponsiveContainer
                width='100%'
                height='100%'
                minWidth={1}
                minHeight={1}
              >
                <BarChart
                  data={processedLaps}
                  layout='vertical'
                  barSize={barSize * 0.9} // Fixed horizontal width for bars
                  barCategoryGap={2} // Set a fixed pixel value for the gap between bars
                  barGap={0} // No gap between bars within the same category
                >
                  <XAxis type='number' dataKey='averageSpeed' hide />
                  <YAxis type='category' dataKey='id' hide />
                  <Bar dataKey='averageSpeed' fill='#0f766e' />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
